import { useEffect, useRef } from 'react'
import './RichText.css'

/**
 * The message body: bold, italic, underline, a bullet list and a link.
 *
 * ## Uncontrolled on purpose
 *
 * A `contentEditable` whose `innerHTML` is written back from React state on
 * every keystroke puts the caret at position zero after each character. So the
 * element owns its own content: React writes it once on mount, reads it on
 * input, and never writes again unless `resetKey` changes.
 *
 * ## execCommand, deprecated and still correct here
 *
 * It is the only thing that applies formatting to a selection without writing a
 * selection model, a command stack and a serialiser — a text editor, in other
 * words, for five buttons. Every browser still implements it, and the failure
 * mode if one stops is that a button does nothing, not that a send breaks.
 *
 * ## The output is email HTML
 *
 * Which is why the toolbar is this short. Bold, italic, underline, lists and
 * links survive Outlook and Gmail. Headings, colours and font sizes produce
 * markup that renders differently in every client, so they are not offered
 * rather than offered and disappointing.
 */
export function RichText({
  value,
  onChange,
  resetKey,
}: {
  value: string
  onChange: (html: string) => void
  /** Change this to force the editor to take `value` again. */
  resetKey?: string | number
}) {
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (box.current && box.current.innerHTML !== value) box.current.innerHTML = value
    // Deliberately not depending on `value` — see the note above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey])

  const cmd = (command: string, arg?: string) => {
    // Focus first: a command applied while the toolbar button holds focus has
    // no selection to act on and silently does nothing.
    box.current?.focus()
    document.execCommand(command, false, arg)
    if (box.current) onChange(box.current.innerHTML)
  }

  const link = () => {
    const url = window.prompt('Link to')
    if (!url) return
    // Anything without a scheme becomes a relative link in an email client,
    // which resolves against nothing and silently fails to open.
    cmd('createLink', /^[a-z][a-z0-9+.-]*:/i.test(url) ? url : `https://${url}`)
  }

  return (
    <div className="rtx">
      <div className="rtx__bar">
        <button className="rtx__btn rtx__btn--b" onClick={() => cmd('bold')} title="Bold">
          B
        </button>
        <button className="rtx__btn rtx__btn--i" onClick={() => cmd('italic')} title="Italic">
          I
        </button>
        <button className="rtx__btn rtx__btn--u" onClick={() => cmd('underline')} title="Underline">
          U
        </button>
        <button
          className="rtx__btn"
          onClick={() => cmd('insertUnorderedList')}
          title="Bulleted list"
        >
          •≡
        </button>
        <button className="rtx__btn" onClick={link} title="Insert link">
          🔗
        </button>
      </div>
      <div
        ref={box}
        className="rtx__body"
        contentEditable
        role="textbox"
        aria-multiline="true"
        aria-label="Message"
        suppressContentEditableWarning
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        // Pasting from Word or a web page brings its stylesheet with it, which
        // is where most unsendable email HTML comes from. Plain text only; the
        // toolbar is how formatting gets in.
        onPaste={(e) => {
          e.preventDefault()
          const text = e.clipboardData.getData('text/plain')
          document.execCommand('insertText', false, text)
        }}
      />
    </div>
  )
}
